import { useEffect } from "react";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Politique de confidentialité · FlowMind AI";
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PublicHeader />
      <section className="max-w-3xl mx-auto px-6 py-20" data-testid="privacy-policy-page">
        <span className="overline">RGPD · Protection des données</span>
        <h1 className="font-display text-5xl font-medium tracking-tighter mt-4 mb-4">Politique de confidentialité</h1>
        <p className="text-sm text-zinc-500 mb-12">Dernière mise à jour : 10 février 2026</p>

        <article className="prose prose-zinc max-w-none">
          <p className="text-zinc-700 leading-relaxed">
            FlowMind AI SAS (« nous », « notre », « FlowMind AI ») s'engage à protéger la vie privée de ses
            utilisateurs conformément au Règlement Général sur la Protection des Données (RGPD — UE 2016/679) et
            à la loi française Informatique & Libertés.
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">1. Responsable du traitement</h2>
          <p className="text-zinc-700 leading-relaxed">
            <strong>FlowMind AI SAS</strong>, 1 rue de Rivoli, 75001 Paris, France.<br/>
            DPO : <strong>dpo@flowmindai.eu</strong>
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">2. Données collectées</h2>
          <ul className="list-disc pl-6 text-zinc-700 space-y-2 mt-3">
            <li><strong>Données de compte</strong> : nom, prénom, email, mot de passe (hashé), nom de l'entreprise.</li>
            <li><strong>Données de facturation</strong> : adresse, informations TVA (paiements traités via Stripe).</li>
            <li><strong>Données d'utilisation</strong> : workflows créés, exécutions, logs techniques anonymisés.</li>
            <li><strong>Données techniques</strong> : adresse IP, type de navigateur, pages visitées (analytique anonymisée).</li>
          </ul>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">3. Finalités du traitement</h2>
          <ul className="list-disc pl-6 text-zinc-700 space-y-2 mt-3">
            <li>Fournir et améliorer le service FlowMind AI.</li>
            <li>Gérer votre compte, votre abonnement et la facturation.</li>
            <li>Envoyer des emails transactionnels (création de compte, factures, alertes workflow).</li>
            <li>Assurer la sécurité de la plateforme et prévenir les fraudes.</li>
            <li>Respecter nos obligations légales et comptables.</li>
          </ul>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">4. Base légale</h2>
          <p className="text-zinc-700 leading-relaxed">
            Le traitement repose sur (i) l'exécution du contrat de service, (ii) le consentement explicite pour
            les cookies non essentiels et la newsletter, et (iii) le respect d'obligations légales (facturation, comptabilité).
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">5. Hébergement & localisation</h2>
          <p className="text-zinc-700 leading-relaxed">
            <strong>L'ensemble de vos données est stocké exclusivement dans des datacenters situés en Union Européenne</strong>
            {" "}(région eu-west-3, Paris). Aucune donnée n'est transférée vers des pays tiers sans garanties appropriées
            (clauses contractuelles types de la Commission Européenne).
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">6. Sous-traitants</h2>
          <p className="text-zinc-700 leading-relaxed">FlowMind AI fait appel aux sous-traitants suivants :</p>
          <ul className="list-disc pl-6 text-zinc-700 space-y-2 mt-3">
            <li><strong>Stripe Payments Europe Ltd.</strong> (Irlande) — Traitement des paiements.</li>
            <li><strong>Resend</strong> — Envoi d'emails transactionnels (DPA signé, infra UE).</li>
            <li><strong>Anthropic</strong> — Génération IA (les prompts ne sont pas utilisés pour l'entraînement, voir leurs CGU).</li>
            <li><strong>Twilio</strong> — Messagerie WhatsApp Business (uniquement si activé par l'utilisateur).</li>
          </ul>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">7. Durée de conservation</h2>
          <ul className="list-disc pl-6 text-zinc-700 space-y-2 mt-3">
            <li>Données de compte : durée du contrat + 3 ans après désactivation.</li>
            <li>Données de facturation : 10 ans (obligation comptable).</li>
            <li>Logs techniques : 12 mois maximum.</li>
            <li>Cookies analytiques : 13 mois maximum.</li>
          </ul>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">8. Vos droits</h2>
          <p className="text-zinc-700 leading-relaxed">Conformément au RGPD, vous disposez des droits suivants :</p>
          <ul className="list-disc pl-6 text-zinc-700 space-y-2 mt-3">
            <li>Droit d'accès à vos données</li>
            <li>Droit de rectification</li>
            <li>Droit à l'effacement (« droit à l'oubli »)</li>
            <li>Droit à la limitation du traitement</li>
            <li>Droit à la portabilité</li>
            <li>Droit d'opposition</li>
            <li>Droit de retirer votre consentement à tout moment</li>
          </ul>
          <p className="text-zinc-700 leading-relaxed mt-4">
            Pour exercer ces droits : <strong>dpo@flowmindai.eu</strong>. Réponse sous 30 jours maximum.
            Vous pouvez également déposer une réclamation auprès de la CNIL (<a href="https://www.cnil.fr" className="underline" target="_blank" rel="noopener noreferrer">cnil.fr</a>).
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">9. Sécurité</h2>
          <p className="text-zinc-700 leading-relaxed">
            Chiffrement TLS 1.3 en transit, AES-256 au repos. Mots de passe hashés (bcrypt). Authentification JWT
            avec rotation. Sauvegardes chiffrées quotidiennes. Audits de sécurité réguliers.
          </p>

          <h2 className="font-display text-2xl font-medium tracking-tight mt-10 mb-4">10. Notification de violation</h2>
          <p className="text-zinc-700 leading-relaxed">
            En cas de violation de données susceptible d'engendrer un risque pour vos droits et libertés, nous
            notifierons la CNIL dans un délai de 72 heures et vous informerons sans délai injustifié.
          </p>
        </article>
      </section>
      <PublicFooter />
    </div>
  );
}
